import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../Ventas.css'; // Utilizando los estilos mejorados
import API from '../../../../utils/api';
import {
  Calendar,
  ChevronDown,
  ArrowDown,
  ArrowUp,
  DollarSign,
  Users,
  Clock,
  ChevronRight,
  CircleCheck,
  List,
  Grid,
  RefreshCw,
  PlusCircle,
  Eye,
  Truck,
  Bot
} from 'lucide-react';

const PedidosDelibot = () => {
  const navigate = useNavigate();

  // Estados principales
  const [activeStatusTab, setActiveStatusTab] = useState('en_proceso');
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'tabla' o 'grid'
  const [pedidosFiltrados, setPedidosFiltrados] = useState([]);

  // Función para cargar los pedidos según el estado seleccionado
  const fetchPedidos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar que el método API exista
      if (typeof API.pedidos?.getPedidosActivos !== 'function') {
        throw new Error('La función API.pedidos.getPedidosDelibot no está disponible');
      }
      let tipoFiltro = 'delibot';

      // Para la tab "en_proceso" necesitamos obtener tanto abiertos como pendientes
      let estadoFiltro = activeStatusTab;
      if (activeStatusTab === 'en_proceso') {
        // Para en_proceso, usamos 'todos' y luego filtramos manualmente
        estadoFiltro = 'todos_db';
      }

      // Llamar a la API para obtener los pedidos
      const pedidosData = await API.pedidos.getPedidosActivos(tipoFiltro, estadoFiltro);

      // Ordenar por fecha, más recientes primero
      const pedidosOrdenados = pedidosData.sort((a, b) => {
        return new Date(b.fechaCreacion) - new Date(a.fechaCreacion);
      });

      setPedidos(pedidosOrdenados);

      // Filtramos los pedidos según la tab seleccionada
      if (activeStatusTab === 'en_proceso') {
        // Para "En Proceso" mostramos tanto abiertos como pendientes
        const filtrados = pedidosOrdenados.filter(pedido =>
          pedido.status === 'abierto' || pedido.status === 'pendiente'
        );
        setPedidosFiltrados(filtrados);
      } else {
        // Para las demás tabs, usamos todos los pedidos tal cual
        setPedidosFiltrados(pedidosOrdenados);
      }
    } catch (err) {
      console.error("Error al cargar pedidos Delibot:", err);
      setError("No se pudieron cargar los pedidos. Intente de nuevo.");

      // En caso de error, usar datos de muestra
      const pedidosMuestra = [
        // Aquí se pueden agregar pedidos de muestra si es necesario
      ];

      setPedidos(pedidosMuestra);
      setPedidosFiltrados(pedidosMuestra);
    } finally {
      setLoading(false);
    }
  }, [activeStatusTab]);

  // Efecto para cargar pedidos cuando cambia la pestaña
  useEffect(() => {
    fetchPedidos();
  }, [activeStatusTab, fetchPedidos]);

  // Manejadores de eventos
  const handleStatusTabChange = useCallback((tab) => {
    setActiveStatusTab(tab);
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode(prevMode => prevMode === 'tabla' ? 'grid' : 'tabla');
  }, []);

  const handleNuevoPedido = useCallback(() => {
    // Navegar a nuevo pedido sin localStorage
    navigate('/delibot-nuevo');
  }, [navigate]);

  const handleContinuarPedido = useCallback((pedido) => {
    const datosPedido = {
      numeroDelibot: pedido.numeroDelibot || pedido.clienteLlevar || pedido.clienteDelibot || 'DEL-000001',
      pedidoCreado: pedido,
      tipoServicio: 'delibot',
      idPedido: pedido.idPedido,
      numeroPedido: pedido.numeroDia,
      esEdicion: true
    };

    // Navegar con datos del pedido en el estado
    navigate('/delibot-nuevo', { state: { datosPedido } });
  }, [navigate]);

  const handleCambiarEstado = useCallback(async (idPedido, nuevoEstado) => {
    try {
      setLoadingAction(true);

      if (typeof API.pedidos?.cambiarEstadoPedido !== 'function') {
        throw new Error('La función API.pedidos.cambiarEstadoPedido no está disponible');
      }

      await API.pedidos.cambiarEstadoPedido(idPedido, nuevoEstado);
      fetchPedidos();

    } catch (error) {
      console.error("Error al cambiar estado del pedido:", error);
      alert("No se pudo cambiar el estado del pedido. Intente de nuevo.");
    } finally {
      setLoadingAction(false);
    }
  }, [fetchPedidos]);

  // Formatear fecha y hora
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  // Formatear monto como moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  // Obtener color basado en el estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'abierto': return '#22c55e';
      case 'pendiente': return '#f59e0b';
      case 'cerrado': return '#3b82f6';
      default: return '#64748b';
    }
  };

  // Obtener icono basado en el estado
  const getStatusIcon = (status) => {
    switch (status) {
      case 'abierto': return <Bot size={16} />;
      case 'pendiente': return <Clock size={16} />;
      case 'cerrado': return <CircleCheck size={16} />;
      default: return null;
    }
  };

  // Renderizar componente
  return (
    <div className="dashboard">
      <div className="dashboard-content">
        <div className="pedidos-status-tabs">
          <button
            className={`status-tab-button ${activeStatusTab === 'en_proceso' ? 'active' : ''}`}
            onClick={() => handleStatusTabChange('en_proceso')}
          >
            EN PROCESO
          </button>
          <button
            className={`status-tab-button ${activeStatusTab === 'abierto' ? 'active' : ''}`}
            onClick={() => handleStatusTabChange('abierto')}
          >
            ABIERTOS
          </button>
          <button
            className={`status-tab-button ${activeStatusTab === 'pendiente' ? 'active' : ''}`}
            onClick={() => handleStatusTabChange('pendiente')}
          >
            PENDIENTES
          </button>

        </div>

        <div className="tab-content">
          <div className="pedido-controls">
            <button
              className="nuevo-pedido-button"
              onClick={handleNuevoPedido}
            >
              <PlusCircle size={18} />
              Nuevo Pedido Delibot
            </button>
            <div className="view-mode-switch">
              <span>Vista:</span>
              <button
                className={`view-mode-button ${viewMode === 'tabla' ? 'active' : ''}`}
                onClick={() => setViewMode('tabla')}
                title="Vista de tabla"
              >
                <List size={18} />
              </button>
              <button
                className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Vista de tarjetas"
              >
                <Grid size={18} />
              </button>
            </div>

            <button
              className="refresh-button"
              onClick={fetchPedidos}
              disabled={loading}
            >
              <RefreshCw size={16} />
              {loading ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading">Cargando pedidos...</div>
            </div>
          ) : pedidosFiltrados.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🤖</div>
              <h3>No hay pedidos {activeStatusTab === 'en_proceso' ? 'en proceso' : activeStatusTab}</h3>
              <p>
                {activeStatusTab === 'abierto' && 'Crea un nuevo pedido Delibot usando el botón superior.'}
                {activeStatusTab === 'pendiente' && 'No hay pedidos pendientes de pago en este momento.'}
                {activeStatusTab === 'en_proceso' && 'No hay pedidos abiertos ni pendientes en este momento.'}
                {activeStatusTab === 'cerrado' && 'No hay pedidos pagados en el período actual.'}
              </p>
            </div>
          ) : (
            <>
              {viewMode === 'tabla' && (
                <div className="pedidos-lista">
                  <table className="pedidos-table">
                    <thead>
                      <tr>
                        <th>Número</th>
                        <th>ID Delibot</th>
                        <th>Total</th>
                        <th>Fecha</th>
                        <th>Abierto</th>
                        <th>Impreso</th>
                        <th>Cerrado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pedidosFiltrados.map(pedido => (
                        <tr
                          key={pedido.idPedido || pedido.id}
                          className={`pedido-row ${pedido.status} pedido-clickable`}
                          onClick={() => handleContinuarPedido(pedido)}
                        >
                          <td>{pedido.numeroDia}</td>
                          <td>{pedido.numeroDelibot || 'Sin ID'}</td>
                          <td>{formatCurrency(pedido.total)}</td>
                          <td>{formatDateTime(pedido.fechaCreacion)}</td>
                          <td>{pedido.horaAbierto}</td>
                          <td>
                            {pedido.horaEnvio && pedido.horaEnvio !== '00:00:00' ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <CircleCheck size={18} color="green" /> {pedido.horaEnvio}
                              </div>
                            ) : "--:--"}
                          </td>
                          <td>
                            {pedido.horaCerrado && pedido.horaCerrado !== '00:00:00' ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <CircleCheck size={18} color="green" /> {pedido.horaCerrado}
                              </div>
                            ) : "--:--"}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              className="action-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleContinuarPedido(pedido);
                              }}
                            >
                              <Eye size={14} style={{ marginRight: '4px' }} /> Ver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewMode === 'grid' && (
                <div className="pedidos-grid">
                  {pedidosFiltrados.map(pedido => {
                    // Extraer información de Delibot si está disponible
                    let delibotID = pedido.clienteLlevar || 'Sin ID';
                    let clienteInfo = '';

                    return (
                      <div
                        key={pedido.idPedido || pedido.id}
                        className={`pedido-card ${pedido.status}`}
                        onClick={() => handleContinuarPedido(pedido)}
                      >
                        <div className="pedido-card-header">
                          <div className="pedido-numero">{pedido.numeroDia}</div>
                          <div className="pedido-fecha">{formatDateTime(pedido.fechaCreacion)}</div>
                        </div>

                        <div className="pedido-card-cliente">
                          <strong>{clienteInfo}</strong>
                          <div style={{
                            fontSize: '0.85rem',
                            color: '#64748b',
                            marginTop: '2px'
                          }}>
                            Delibot ID: {delibotID}
                          </div>
                        </div>

                        <div className="pedido-card-total">
                          <span>{formatCurrency(pedido.total)}</span>
                        </div>

                        <div className="pedido-card-estados">
                          <div className="estado-item">
                            <span>Abierto</span>
                            {pedido.horaAbierto || '--:--'}
                          </div>

                          <div className="estado-item">
                            <span>Impreso</span>
                            {pedido.horaEnvio && pedido.horaEnvio !== '00:00:00' ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <CircleCheck size={16} color="green" /> {pedido.horaEnvio}
                              </div>
                            ) : "--:--"}
                          </div>

                          <div className="estado-item">
                            <span>Cerrado</span>
                            {pedido.horaCerrado && pedido.horaCerrado !== '00:00:00' ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <CircleCheck size={16} color="green" /> {pedido.horaCerrado}
                              </div>
                            ) : "--:--"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Overlay de carga para acciones */}
      {loadingAction && (
        <div className="modal-overlay loading-overlay">
          <div className="loading-spinner">
            <div className="loading">Procesando...</div>
          </div>
        </div>
      )}


    </div>
  );
};

export default PedidosDelibot;